/**
 * Named theme presets — 6 starter looks that swap the entire token set
 * in one click. Defined as { mode, palette, typography, density, radius,
 * pattern } so the editor can either apply a whole preset or mix and
 * match (e.g., Foundry palette + Compact density).
 *
 * Each palette entry is an RGB triple as "R G B" so Tailwind's
 * `rgb(var(--color-x) / <alpha>)` alpha resolution works.
 */

import type { ThemeMode, TenantTheme } from "./types";

export interface ThemePreset {
  id: string;
  name: string;
  blurb: string;
  /** Mode the preset is balanced for; `system` follows OS. */
  mode: ThemeMode;
  /**
   * Partial — only fields the preset wants to set. Anything left
   * undefined falls through to the user's existing overrides /
   * defaults. The admin editor calls `applyPreset()` to apply.
   */
  theme: Partial<TenantTheme>;
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: "default-blue",
    name: "Default Blue",
    blurb: "SAHAS indigo · light",
    mode: "light",
    theme: {
      mode: "light",
      palette: {
        brand: "99 102 241",
        accent: "14 165 233",
        success: "34 197 94",
        warning: "234 179 8",
        danger: "239 68 68",
        info: "59 130 246",
      },
      typography: "inter",
      density: "comfortable",
      radius: "soft",
      pattern: "none",
    },
  },
  {
    id: "versatile",
    name: "Versatile",
    blurb: "Versatile, powered by Sahas · teal",
    mode: "light",
    theme: {
      mode: "light",
      palette: {
        brand: "13 148 136", // teal-600
        accent: "15 118 110", // teal-700
        success: "34 197 94",
        warning: "234 179 8",
        danger: "239 68 68",
        info: "59 130 246",
      },
      typography: "inter",
      density: "comfortable",
      radius: "soft",
      pattern: "none",
    },
  },
  {
    id: "foundry-amber",
    name: "Foundry Amber",
    blurb: "Molten iron · light",
    mode: "light",
    theme: {
      mode: "light",
      palette: {
        brand: "234 88 12", // orange-600
        accent: "217 119 6", // amber-600
        success: "16 185 129",
        warning: "234 179 8",
        danger: "220 38 38",
        info: "245 158 11",
      },
      typography: "plex",
      density: "comfortable",
      radius: "sharp",
      pattern: "grid",
    },
  },
  {
    id: "graphite",
    name: "Graphite",
    blurb: "Neutral steel · light",
    mode: "light",
    theme: {
      mode: "light",
      palette: {
        brand: "71 85 105", // slate-600
        accent: "100 116 139", // slate-500
        success: "34 197 94",
        warning: "245 158 11",
        danger: "239 68 68",
        info: "59 130 246",
      },
      typography: "inter",
      density: "compact",
      radius: "sharp",
      pattern: "none",
    },
  },
  {
    id: "midnight",
    name: "Midnight",
    blurb: "Deep navy · best with dark mode",
    mode: "dark",
    theme: {
      mode: "dark",
      palette: {
        brand: "129 140 248", // indigo-400
        accent: "56 189 248", // sky-400
        success: "74 222 128",
        warning: "250 204 21",
        danger: "248 113 113",
        info: "96 165 250",
      },
      typography: "inter",
      density: "comfortable",
      radius: "soft",
      pattern: "dots",
    },
  },
  {
    id: "forest",
    name: "Forest",
    blurb: "Sustainability · light",
    mode: "light",
    theme: {
      mode: "light",
      palette: {
        brand: "22 163 74", // green-600
        accent: "5 150 105", // emerald-600
        success: "16 185 129",
        warning: "245 158 11",
        danger: "239 68 68",
        info: "20 184 166",
      },
      typography: "source",
      density: "comfortable",
      radius: "soft",
      pattern: "topo",
    },
  },
  {
    id: "plum",
    name: "Plum",
    blurb: "Pharmaceutical and biotech · light",
    mode: "light",
    theme: {
      mode: "light",
      palette: {
        brand: "147 51 234", // purple-600
        accent: "168 85 247", // purple-500
        success: "16 185 129",
        warning: "245 158 11",
        danger: "236 72 153",
        info: "139 92 246",
      },
      typography: "plex",
      density: "comfortable",
      radius: "round",
      pattern: "none",
    },
  },
];

export function getPreset(id: string): ThemePreset | undefined {
  return THEME_PRESETS.find((p) => p.id === id);
}
